import { useState, useEffect, useCallback, useMemo } from 'react';
import { holidayService } from 'api/services/holidayService';
import type {
  ID, PaginatedResponse, Holiday,
  CreateHolidayRequest, UpdateHolidayRequest, HolidaySearchRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0,
  hasNextPage: false, hasPreviousPage: false,
};

export const useHolidays = (params?: HolidaySearchRequest) => {
  const [data, setData] = useState<Holiday[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<Holiday>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchHolidays = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await holidayService.getAll(params);
      setData(response.data); setPagination(response.pagination);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);
  return { data, pagination, loading, error, refetch: fetchHolidays };
};

export const useHoliday = (id: ID | null | undefined) => {
  const [data, setData] = useState<Holiday | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchHoliday = useCallback(async () => {
    if (!id) { setData(null); return; }
    setLoading(true); setError(null);
    try { const r = await holidayService.getById(id); setData(r.data); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { fetchHoliday(); }, [fetchHoliday]);
  return { data, loading, error, refetch: fetchHoliday };
};

export const useCreateHoliday = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const createHoliday = async (data: CreateHolidayRequest, options?: { onSuccess?: (item: Holiday) => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { const r = await holidayService.create(data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { createHoliday, loading, error };
};

export const useUpdateHoliday = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const updateHoliday = async (id: ID, data: UpdateHolidayRequest, options?: { onSuccess?: (item: Holiday) => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { const r = await holidayService.update(id, data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { updateHoliday, loading, error };
};

export const useDeleteHoliday = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const deleteHoliday = async (id: ID, options?: { onSuccess?: () => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { await holidayService.delete(id); options?.onSuccess?.(); }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { deleteHoliday, loading, error };
};
